// MobileContextMenuOverlay — renders a floating menu at the long-press
// origin when MobileContextMenuContext state is non-null.
//
// Why not Radix ContextMenu / DropdownMenu? Both rely on a trigger
// element + pointer-event tracking, which doesn't work when the
// "trigger" is a virtual point in space rather than a hovered element.
// A plain positioned div is the most reliable cross-browser approach
// for a touch-driven menu and has no dependency on Radix internals.
//
// Items per kind:
//   block:      Edit name, Edit description, Edit color, Details…,
//               Convert to layer, Duplicate, Delete
//   group-card: Edit name, Edit color, Details…, Toggle collapse,
//               Toggle lock, Delete
//   canvas:     Add block submenu (toolTypes), Fit to content, Reset zoom

import { useEffect, useRef } from 'react';
import {
  Pencil, Type, Palette, FileText, Layers, Copy, Trash2,
  Lock, ChevronsRight, Maximize, RefreshCw, Plus,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  useMobileContextMenu,
  type MobileCtxMenuKind,
} from '@/contexts/MobileContextMenuContext';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useEntityDetailsDialog } from '@/contexts/useEntityDetailsDialog';
import { useTranslation } from '@/i18n/LanguageContext';

const MENU_WIDTH = 240;
/** Estimated menu height. Used only for vertical clamping; the menu
 *  resizes its own height by content. */
const MENU_HEIGHT_ESTIMATE = 320;

export interface MobileContextMenuOverlayProps {
  /** Block-side handlers, plumbed from the same source as the desktop
   *  BlockContextMenu (Index.tsx). All optional — items hide when
   *  their handler is missing. */
  onFocusBlockProperty?: (blockId: string, field: 'name' | 'description' | 'color') => void;
  onConvertToLayer?: (blockId: string) => void;
  onDuplicateBlock?: (blockId: string) => void;
  onDeleteBlock?: (blockId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  onToggleGroupCollapse?: (groupId: string) => void;
  onToggleGroupLock?: (groupId: string) => void;
  /** Tool types available for the canvas "Add block" submenu. */
  toolTypes?: Array<{ type: string; label: string }>;
  onAddBlock?: (type: string, worldX: number, worldY: number) => void;
  onFitToContent?: () => void;
  onResetZoom?: () => void;
}

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  destructive?: boolean;
  separator?: false;
}
interface Separator { separator: true }
type Entry = MenuItem | Separator;

export function MobileContextMenuOverlay(props: MobileContextMenuOverlayProps) {
  const { state, close } = useMobileContextMenu();
  const { t } = useTranslation();
  const { openDetails } = useEntityDetailsDialog();
  const wf = useWorkflow();
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside-tap / Esc.
  useEffect(() => {
    if (!state) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    // Delay by one tick so the long-press release doesn't immediately close us.
    const id = window.setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown);
      document.addEventListener('keydown', onKey);
    }, 50);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [state, close]);

  if (!state) return null;

  // Position with viewport clamping so the menu never overflows the screen.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.min(Math.max(8, state.x), Math.max(8, vw - MENU_WIDTH - 8));
  const top = Math.min(Math.max(8, state.y), Math.max(8, vh - MENU_HEIGHT_ESTIMATE - 8));

  const entries = buildEntries(state.kind, state.id ?? '', props, t, close, {
    openDetails,
    getBlockName: (id) => wf?.blocks?.find?.((b) => b.id === id)?.config?.name,
    getGroupName: (id) => wf?.groups?.find?.((g) => g.id === id)?.name,
  });
  if (entries.length === 0) return null;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className="fixed z-[100] bg-popover text-popover-foreground border border-border rounded-md shadow-lg overflow-hidden"
      style={{ left, top, width: MENU_WIDTH }}
      onClick={(e) => e.stopPropagation()}
    >
      <ul className="py-1">
        {entries.map((e, i) => {
          if ('separator' in e) {
            return <li key={`sep-${i}`} className="h-px bg-border my-1" aria-hidden="true" />;
          }
          return (
            <li key={i}>
              <button
                type="button"
                role="menuitem"
                onClick={() => { e.onClick(); }}
                className={
                  'flex items-center gap-2 w-full px-3 py-2.5 text-sm text-left transition-colors active:bg-muted ' +
                  (e.destructive ? 'text-destructive' : '')
                }
                style={{ minHeight: 44 }}
              >
                {e.icon}
                <span>{e.label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>,
    document.body,
  );
}

interface BuildCtx {
  openDetails: (opts: { entityId: string; entityType: 'block' | 'group' | 'connector'; entityName?: string }) => void;
  getBlockName: (id: string) => string | undefined;
  getGroupName: (id: string) => string | undefined;
}

function buildEntries(
  kind: MobileCtxMenuKind,
  id: string,
  p: MobileContextMenuOverlayProps,
  t: (key: string) => string,
  close: () => void,
  ctx: BuildCtx,
): Entry[] {
  switch (kind) {
    case 'block':
      return buildBlockEntries(id, p, t, close, ctx);
    case 'group-card':
      return buildGroupCardEntries(id, p, t, close, ctx);
    case 'canvas':
      return buildCanvasEntries(p, t, close);
  }
}

function buildBlockEntries(
  blockId: string,
  p: MobileContextMenuOverlayProps,
  t: (key: string) => string,
  close: () => void,
  ctx: BuildCtx,
): Entry[] {
  const out: Entry[] = [];
  const wrap = (fn: () => void) => () => { fn(); close(); };
  if (p.onFocusBlockProperty) {
    out.push(
      { icon: <Type className="w-4 h-4" />, label: t('editName') || 'Edit name',
        onClick: wrap(() => p.onFocusBlockProperty!(blockId, 'name')) },
      { icon: <Pencil className="w-4 h-4" />, label: t('editDescription') || 'Edit description',
        onClick: wrap(() => p.onFocusBlockProperty!(blockId, 'description')) },
      { icon: <Palette className="w-4 h-4" />, label: t('editColor') || 'Edit color',
        onClick: wrap(() => p.onFocusBlockProperty!(blockId, 'color')) },
    );
  }
  out.push({
    icon: <FileText className="w-4 h-4" />,
    label: t('details') || 'Details…',
    onClick: wrap(() => {
      ctx.openDetails({
        entityId: blockId,
        entityType: 'block',
        entityName: ctx.getBlockName(blockId),
      });
    }),
  });
  if (p.onConvertToLayer) {
    out.push({ separator: true });
    out.push({
      icon: <Layers className="w-4 h-4" />,
      label: t('convertToLayer') || 'Convert to layer',
      onClick: wrap(() => p.onConvertToLayer!(blockId)),
    });
  }
  if (p.onDuplicateBlock) {
    out.push({
      icon: <Copy className="w-4 h-4" />,
      label: t('duplicate') || 'Duplicate',
      onClick: wrap(() => p.onDuplicateBlock!(blockId)),
    });
  }
  if (p.onDeleteBlock) {
    out.push({ separator: true });
    out.push({
      icon: <Trash2 className="w-4 h-4" />,
      label: t('delete') || 'Delete',
      onClick: wrap(() => p.onDeleteBlock!(blockId)),
      destructive: true,
    });
  }
  return out;
}

function buildGroupCardEntries(
  groupId: string,
  p: MobileContextMenuOverlayProps,
  t: (key: string) => string,
  close: () => void,
  ctx: BuildCtx,
): Entry[] {
  const out: Entry[] = [];
  const wrap = (fn: () => void) => () => { fn(); close(); };
  out.push({
    icon: <FileText className="w-4 h-4" />,
    label: t('details') || 'Details…',
    onClick: wrap(() => {
      ctx.openDetails({
        entityId: groupId,
        entityType: 'group',
        entityName: ctx.getGroupName(groupId),
      });
    }),
  });
  if (p.onToggleGroupCollapse) {
    out.push({
      icon: <ChevronsRight className="w-4 h-4" />,
      label: t('toggleCollapse') || 'Toggle collapse',
      onClick: wrap(() => p.onToggleGroupCollapse!(groupId)),
    });
  }
  if (p.onToggleGroupLock) {
    out.push({
      icon: <Lock className="w-4 h-4" />,
      label: t('toggleLock') || 'Toggle lock',
      onClick: wrap(() => p.onToggleGroupLock!(groupId)),
    });
  }
  if (p.onDeleteGroup) {
    out.push({ separator: true });
    out.push({
      icon: <Trash2 className="w-4 h-4" />,
      label: t('deleteGroup') || 'Delete group',
      onClick: wrap(() => p.onDeleteGroup!(groupId)),
      destructive: true,
    });
  }
  return out;
}

function buildCanvasEntries(
  p: MobileContextMenuOverlayProps,
  t: (key: string) => string,
  close: () => void,
): Entry[] {
  const out: Entry[] = [];
  const wrap = (fn: () => void) => () => { fn(); close(); };
  const tools = p.toolTypes ?? [];
  if (tools.length > 0 && p.onAddBlock) {
    for (const tool of tools.slice(0, 8)) {
      out.push({
        icon: <Plus className="w-4 h-4" />,
        label: t(tool.label) || tool.label,
        onClick: wrap(() => p.onAddBlock!(tool.type, 0, 0)),
      });
    }
  }
  if (p.onFitToContent || p.onResetZoom) {
    if (out.length > 0) out.push({ separator: true });
    if (p.onFitToContent) {
      out.push({
        icon: <Maximize className="w-4 h-4" />,
        label: t('fitToContent') || 'Fit to content',
        onClick: wrap(p.onFitToContent),
      });
    }
    if (p.onResetZoom) {
      out.push({
        icon: <RefreshCw className="w-4 h-4" />,
        label: t('resetZoom') || 'Reset zoom',
        onClick: wrap(p.onResetZoom),
      });
    }
  }
  return out;
}

